import dayjs from 'dayjs';
import _ from 'lodash';
import { ValueType } from 'recharts/types/component/DefaultTooltipContent';

import {
  StatisticsAssetResult,
  StatisticsStorageResult,
  StatisticsTypeResult,
} from 'redux/reducers/type';

export function bytesToGBOrTB(bytes: number) {
  const GB = 1024 ** 3;
  const TB = 1024 ** 4;

  if (bytes >= TB) {
    return (bytes / TB).toFixed(2) + ' TB';
  } else {
    return (bytes / GB).toFixed(2) + ' GB';
  }
}

// 비어있는 라인 차트 데이터를 만드는 함수
export function makeEmptyLineChartData(startDate: Date, endDate: Date) {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const result = [];

  const diffInMonths = end.diff(start, 'month');

  if (diffInMonths >= 1) {
    let current = start.startOf('month');
    while (current.isBefore(end) || current.isSame(end, 'month')) {
      const year = current.year();
      const month = current.month() + 1;
      result.push({ name: `${year}.${month}`, value: 0 });
      current = current.add(1, 'month');
    }
  } else {
    let current = start.startOf('day');
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      const month = current.month() + 1;
      const day = current.date();
      result.push({ name: `${month}.${day}`, value: 0 });
      current = current.add(1, 'day');
    }
  }

  return result;
}

// 파이 차트 데이터를 만드는 함수
export function makePieChartAssetData(
  isLoading: boolean,
  statisticsData: StatisticsAssetResult[],
  setStateFunction: React.Dispatch<React.SetStateAction<PieChartAssetData[]>>,
  setTotalFunction: React.Dispatch<React.SetStateAction<PieTotalAssetData>>,
) {
  if (isLoading) {
    return;
  }

  if (statisticsData.length > 0) {
    // 차트에 그려지는 수치
    const available = {
      name: '사용 가능 에셋',
      count: statisticsData[0].AVAILABLE.COUNT,
      fileSize: statisticsData[0].AVAILABLE.FILE_SIZE,
    };

    const deleted = {
      name: '삭제된 에셋',
      count: statisticsData[0].DELETED.COUNT,
      fileSize: statisticsData[0].DELETED.FILE_SIZE,
    };
    setStateFunction([available, deleted]);

    // 차트 중앙에 표시되는 수치
    const count = statisticsData[0].TOTAL.COUNT;
    const fileSize = statisticsData[0].TOTAL.FILE_SIZE;
    setTotalFunction({ count, fileSize });
  }
}

export function makePieChartStorageData(
  isLoading: boolean,
  statisticsData: StatisticsStorageResult[],
  index: number,
  setStateFunction: React.Dispatch<React.SetStateAction<PieChartStorageData[]>>,
  setTotalFunction: React.Dispatch<React.SetStateAction<PieTotalStorageData>>,
) {
  if (isLoading) {
    return;
  }

  if (statisticsData.length > 0) {
    // 차트에 그려지는 수치
    const usable = {
      name: '사용 가능',
      space: statisticsData[index].USABLE_SPACE,
    };

    const used = {
      name: '사용중',
      space: statisticsData[index].USED_SPACE,
    };
    setStateFunction([usable, used]);

    // 차트 중앙에 표시되는 수치
    const total = statisticsData[index].TOTAL_SPACE;
    setTotalFunction({ total });
  }
}

// 라인 차트 데이터를 만드는 함수
export function makeLineChartData(
  isLoading: boolean,
  statisticsTypeData: StatisticsTypeResult[],
  dateFrom: Date,
  dateTo: Date,
  setStateFunction: React.Dispatch<React.SetStateAction<LineChartData[]>>,
) {
  if (isLoading) {
    return;
  }

  if (statisticsTypeData.length > 0) {
    const copy = _.cloneDeep(statisticsTypeData);
    const sortedStatisticsData = copy.sort(
      (a, b) => dayjs(a.DATE).valueOf() - dayjs(b.DATE).valueOf(),
    );

    const mappedStatisticsData =
      statisticsTypeData[0].DATE_UNIT === 'DAILY'
        ? fillMissingDates(
            dayjs(dateFrom).format('YYYYMMDD'),
            dayjs(dateTo).format('YYYYMMDD'),
            sortedStatisticsData,
          ).map(v => {
            const day = dayjs(v.DATE);
            const name = `${day.month() + 1}.${day.date()}`;

            return { name, value: v.COUNT };
          })
        : fillMissingMonths(
            dayjs(dateFrom).format('YYYYMMDD'),
            dayjs(dateTo).format('YYYYMMDD'),
            sortedStatisticsData,
          ).map(v => {
            const day = dayjs(v.DATE);
            const name = `${day.year()}.${day.month() + 1}`;

            return { name, value: v.COUNT };
          });

    setStateFunction(mappedStatisticsData);
  } else if (statisticsTypeData.length === 0) {
    const emptyData = makeEmptyLineChartData(dateFrom, dateTo);

    setStateFunction(emptyData);
  }
}

// MONTHLY data에서 count가 0인 달을 만들어 배열을 리턴하는 함수
function fillMissingMonths(startDate: string, endDate: string, data: StatisticsTypeResult[]) {
  const filledData = [];
  const start = dayjs(startDate, 'YYYYMMDD').startOf('month');
  const end = dayjs(endDate, 'YYYYMMDD').endOf('month');

  const dataMap = new Map();
  data.forEach(item => {
    const monthKey = dayjs(item.DATE).format('YYYYMM');
    dataMap.set(monthKey, {
      ...item,
      DATE: dayjs(item.DATE).startOf('month').format('YYYY-MM-DDTHH:mm:ss'),
    });
  });

  let currentMonth = start;

  while (currentMonth.isBefore(end) || currentMonth.isSame(end, 'month')) {
    const monthKey = currentMonth.format('YYYYMM');

    if (dataMap.has(monthKey)) {
      filledData.push(dataMap.get(monthKey));
    } else {
      filledData.push({
        DATE: currentMonth.startOf('month').format('YYYY-MM-DDTHH:mm:ss'),
        DATE_UNIT: 'MONTHLY',
        COUNT: 0,
      });
    }

    currentMonth = currentMonth.add(1, 'month');
  }

  return filledData;
}

// DAILY data에서 count가 0인 날을 만들어 배열을 리턴하는 함수
function fillMissingDates(startDate: string, endDate: string, data: StatisticsTypeResult[]) {
  const filledData = [];
  const start = dayjs(startDate, 'YYYYMMDD');
  const end = dayjs(endDate, 'YYYYMMDD');

  const dataMap = new Map();
  data.forEach(item => {
    const dateKey = dayjs(item.DATE).format('YYYYMMDD');
    dataMap.set(dateKey, {
      ...item,
      DATE: dayjs(item.DATE).format('YYYY-MM-DDTHH:mm:ss'), // 포맷을 맞춤
    });
  });

  let currentDate = start;

  while (currentDate.isBefore(end) || currentDate.isSame(end)) {
    const dateKey = currentDate.format('YYYYMMDD');

    if (dataMap.has(dateKey)) {
      filledData.push(dataMap.get(dateKey));
    } else {
      filledData.push({
        DATE: currentDate.format('YYYY-MM-DDTHH:mm:ss'),
        DATE_UNIT: 'DAILY',
        COUNT: 0,
      });
    }

    currentDate = currentDate.add(1, 'day');
  }

  return filledData;
}

export function formatNumberWithComma(num: ValueType) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatTooltipLabel(label: string) {
  const array = label.split('.');
  if (array[0].length === 4) {
    return `${array[0]}년 ${array[1]}월`;
  }
  return `${array[0]}월 ${array[1]}일`;
}
